import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import {
  getPatientName,
  getStatusBadge,
  isUpcomingAppointment,
} from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { BookOpen, CalendarDays, ChevronRight, Pencil } from 'lucide-react';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { type AppointmentWithDetails } from 'types/appointments';
import { formatUnifiedForDisplay, isTodayForUser, isYesterdayForUser } from 'utils/date';
import { useFetchRecentSessions } from './useFetchRecentSessions';

const formatHomeDateTime = (dateTime: string): string => {
  const time = formatUnifiedForDisplay(dateTime, "HH'h'mm");

  if (isTodayForUser(dateTime)) {
    return `${t('HomePage.date.today', { ns: LOCIZE_NS.INSURANCE })} ${time}`;
  }

  if (isYesterdayForUser(dateTime)) {
    return `${t('HomePage.date.yesterday', { ns: LOCIZE_NS.INSURANCE })} ${time}`;
  }

  return formatUnifiedForDisplay(dateTime, "dd/MM/yyyy HH'h'mm");
};

const getColumns = (
  onSessionAction: (appointment: AppointmentWithDetails) => void
): ColumnDef<AppointmentWithDetails>[] => [
  {
    id: 'dateTime',
    accessorKey: 'dateTime',
    enableSorting: true,
    header: t('AppointmentsTable.date.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string => formatHomeDateTime(row.original.dateTime),
  },
  {
    id: 'speciality',
    accessorKey: 'specialitiesBookable',
    header: t('AppointmentPdf.Speciality.heading', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string =>
      t(`AppointmentPdf.Speciality.options.${row.original.specialitiesBookable}`, {
        ns: LOCIZE_NS.APPOINTMENTS,
      }),
  },
  {
    id: 'patient',
    accessorFn: row => getPatientName(row.patient),
    enableSorting: true,
    header: t('InsuranceAppointmentsList.patient.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): ReactElement => (
      <span className="block max-w-36 truncate">{getPatientName(row.original.patient)}</span>
    ),
  },
  {
    id: 'status',
    header: t('AppointmentsTable.status.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): ReactElement => getStatusBadge(row.original),
  },
  {
    id: 'actions',
    header: t('AppointmentsTable.actions.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): ReactElement => {
      const appointment = row.original;
      const hasUpcomingAppointment = isUpcomingAppointment(appointment);

      return (
        <Button variant="terciary" icon="leading" onClick={() => onSessionAction(appointment)}>
          {hasUpcomingAppointment ? <Pencil size={8} /> : <BookOpen size={8} />}
          {hasUpcomingAppointment
            ? t('InsuredPersonsList.manageAppointment.cta', { ns: LOCIZE_NS.INSURANCE })
            : t('InsuredPersonsList.bookAppointment.cta', { ns: LOCIZE_NS.INSURANCE })}
        </Button>
      );
    },
  },
];

export const RecentSessions = (): ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();
  const { data: sessions = [], isLoading } = useFetchRecentSessions();

  const columns = useMemo(
    () =>
      getColumns(appointment => {
        if (isUpcomingAppointment(appointment)) {
          navigate(generatePath(ROUTES.PATIENT_APPOINTMENT, { id: appointment.patientId }));
          return;
        }

        navigate(ROUTES.APPOINTMENTS_RESERVE, {
          state: { patientId: appointment.patientId },
        });
      }),
    [navigate]
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <ClientPaginatedTable
        title={t('HomePage.recentSessions.heading')}
        icon={CalendarDays}
        data={sessions}
        columns={columns}
        isLoading={isLoading}
        emptyTitle={t('HomePage.recentSessions.empty')}
        tableClassName="w-full"
        pageSize={5}
        initialSorting={[{ id: 'dateTime', desc: true }]}
      />
      <div className="flex justify-end">
        <Button
          variant="ghost"
          className="w-fit gap-2 p-0 text-teal-600 hover:bg-transparent hover:text-teal-500"
          onClick={() => navigate(ROUTES.APPOINTMENTS)}
        >
          {t('HomePage.viewAll')}
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};
